import { Injector, StaticProvider } from '@angular/core';
import {
	AnalyticsFilters,
	AnalyticsInfoResponse
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-analytics.model';
import { GetSocialListeningMyBrandAnalyticsBestTimeToPostService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-best-time-to-post.service';
import { GetSocialListeningMyBrandAnalyticsEmotionDistributionService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-emotion-distribution.service';
import { GetSocialListeningMyBrandAnalyticsEmotionService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-emotion.service';
import { GetSocialListeningMyBrandAnalyticsInfluencersByMediaTypeService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-influencers-by-media-type.service';
import { GetSocialListeningMyBrandAnalyticsInfoService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-info.service';
import { GetSocialListeningMyBrandAnalyticsLanguagesService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-languages.service';
import { GetSocialListeningMyBrandAnalyticsMentionsByMediaTypeService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-mentions-by-media-type.service';
import { GetSocialListeningMyBrandAnalyticsSentimentByPlatformService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-sentiment-by-platform.service';
import { GetSocialListeningMyBrandAnalyticsSentimentService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-sentiment.service';
import { GetSocialListeningMyBrandAnalyticsTopCountriesService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-countries.service';
import { GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-influencers-by-voice-share.service';
import { GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-sites-by-visits.service';
import { GetSocialListeningMyBrandAnalyticsTrendingConversationsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-trending-conversations.service';
import { GetSocialListeningMyBrandAnalyticsTrendingHashtagsService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-trending-hashtags.service';
import { GetSocialListeningMyBrandAnalyticsVolumeService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-volume.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ReportDateRange } from '../../../../models/preview/preview.models';
import { ReportBlockData } from '../../../../services/report-blocks.service';
import { ReportSharedReadsService } from '../../../../services/report-shared-reads.service';
import { ReportSourceSectionsService } from '../../../../services/report-source-sections.service';
import {
	sourceAuthorsByPlatform,
	sourceBestTimeCells,
	sourceEmotionDistribution,
	sourceEmotionOverTime,
	sourceHeadlineTiles,
	sourceLanguages,
	sourceMentionsByPlatform,
	sourceSentimentByPlatform,
	sourceSentimentOverTime,
	sourceTopCountries,
	sourceTopInfluencers,
	sourceTopSites,
	sourceTrendingConversations,
	sourceTrendingHashtags,
	sourceVolume
} from '../../../../services/report-source-sections';

export interface ReportSourceSectionRead {
	reportId: string;
	sectionId: string;
	sections: ReportSourceSectionsService;
}

type SectionFetch<T> = (
	brandId: string,
	cutoff?: Date | null,
	filters?: AnalyticsFilters,
	to?: Date | null
) => Observable<T>;

// TRAP: only the window of the call is read — the brand it names is not
// whose numbers these are, and a section about a source takes no filters. A
// failed read answers as nothing to draw, as the analytics services do.
function sectionFetch<T>(
	read: ReportSourceSectionRead,
	answer: (blocks: ReportBlockData[]) => T
): SectionFetch<T> {
	return (_brandId, cutoff, _filters, to) =>
		sectionBlocks(read, cutoff, to).pipe(map(answer));
}

function sectionBlocks(
	read: ReportSourceSectionRead,
	from: Date | string | null | undefined,
	to: Date | string | null | undefined
): Observable<ReportBlockData[]> {
	return read.sections
		.section(read.reportId, read.sectionId, from, to)
		.pipe(map(blocks => blocks ?? []));
}

// CONTRACT: no brand to name — the host hands the Executive Summary the
// source's own line for its tiles (its `source` input).
class SourceSharedReads
	implements
		Pick<ReportSharedReadsService, 'kpis' | 'mentionTotal' | 'brandLabel'>
{
	constructor(
		private read: ReportSourceSectionRead,
		private empty: () => AnalyticsInfoResponse
	) {}

	kpis(
		_brandId: string,
		range: ReportDateRange
	): Observable<AnalyticsInfoResponse> {
		return sectionBlocks(this.read, range.start, range.end).pipe(
			map(blocks => sourceHeadlineTiles(blocks) ?? this.empty())
		);
	}

	mentionTotal(
		_brandId: string,
		range: ReportDateRange
	): Observable<number | null> {
		return sectionBlocks(this.read, range.start, range.end).pipe(
			map(blocks => sourceHeadlineTiles(blocks)?.totalMentions ?? null)
		);
	}

	brandLabel(_brandId: string): Observable<string | null> {
		return of(null);
	}
}

// TRAP: every service of every section, whichever slide takes them — the
// Executive Summary reads two, and one left out is read from the BRAND.
export function sourceSlideProviders(
	read: ReportSourceSectionRead,
	parent: Injector
): StaticProvider[] {
	const volume: Pick<
		GetSocialListeningMyBrandAnalyticsVolumeService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceVolume) };
	const sentiment: Pick<
		GetSocialListeningMyBrandAnalyticsSentimentService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceSentimentOverTime) };
	const emotion: Pick<
		GetSocialListeningMyBrandAnalyticsEmotionService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceEmotionOverTime) };
	const sentimentByPlatform: Pick<
		GetSocialListeningMyBrandAnalyticsSentimentByPlatformService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceSentimentByPlatform) };
	const emotionDistribution: Pick<
		GetSocialListeningMyBrandAnalyticsEmotionDistributionService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceEmotionDistribution) };
	const topInfluencers: Pick<
		GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceTopInfluencers) };
	const topSites: Pick<
		GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceTopSites) };
	const authorsByPlatform: Pick<
		GetSocialListeningMyBrandAnalyticsInfluencersByMediaTypeService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceAuthorsByPlatform) };
	const bestTime: Pick<
		GetSocialListeningMyBrandAnalyticsBestTimeToPostService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceBestTimeCells) };
	const topCountries: Pick<
		GetSocialListeningMyBrandAnalyticsTopCountriesService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceTopCountries) };
	const hashtags: Pick<
		GetSocialListeningMyBrandAnalyticsTrendingHashtagsService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceTrendingHashtags) };
	const conversations: Pick<
		GetSocialListeningMyBrandAnalyticsTrendingConversationsService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceTrendingConversations) };
	const mentionsByPlatform: Pick<
		GetSocialListeningMyBrandAnalyticsMentionsByMediaTypeService,
		'fetch'
	> = { fetch: sectionFetch(read, sourceMentionsByPlatform) };
	// TRAP: the top languages only. The answer holds no split by sentiment,
	// and an empty one here would read as a period with none.
	const languages: Pick<
		GetSocialListeningMyBrandAnalyticsLanguagesService,
		'fetchTop'
	> = { fetchTop: sectionFetch(read, sourceLanguages) };
	const sharedReads = new SourceSharedReads(read, () =>
		parent
			.get(GetSocialListeningMyBrandAnalyticsInfoService)
			.emptyResponse()
	);

	return [
		{
			provide: GetSocialListeningMyBrandAnalyticsVolumeService,
			useValue: volume
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsSentimentService,
			useValue: sentiment
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsEmotionService,
			useValue: emotion
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsSentimentByPlatformService,
			useValue: sentimentByPlatform
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsEmotionDistributionService,
			useValue: emotionDistribution
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService,
			useValue: topInfluencers
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsTopSitesByVisitsService,
			useValue: topSites
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsInfluencersByMediaTypeService,
			useValue: authorsByPlatform
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsBestTimeToPostService,
			useValue: bestTime
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsTopCountriesService,
			useValue: topCountries
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsTrendingHashtagsService,
			useValue: hashtags
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsTrendingConversationsService,
			useValue: conversations
		},
		{
			provide:
				GetSocialListeningMyBrandAnalyticsMentionsByMediaTypeService,
			useValue: mentionsByPlatform
		},
		{
			provide: GetSocialListeningMyBrandAnalyticsLanguagesService,
			useValue: languages
		},
		{ provide: ReportSharedReadsService, useValue: sharedReads }
	];
}
